import React, { useState, useEffect, useCallback } from "react";
import ProgressFullBar from "../ProgressFullBar/ProgressFullBar";
import "./ProductOne.scss";
import { useDispatch, useSelector } from "react-redux";
import { getAllCategories } from "../../../../../api/CategoryApi/CategorySlice.js";
import { getallLang } from "../../../../../api/LanguageApi/Lang";
import Select from "react-select";
import { Countries } from "../../../../../api/CountryApi";
import RichTextEditor from "../../../../../Components/RichTextEditor/RichTextEditor";

/* ── common spec label suggestions ─────────────────────────────────── */
const SPEC_SUGGESTIONS = [
    "Capacity", "Watt", "Ampere", "Voltage", "Liter",
    "Weight", "Dimensions", "Material", "Color", "Speed",
    "Pressure", "Temperature", "Frequency", "Power", "Bar",
];

/* ── single spec row ────────────────────────────────────────────────── */
const SpecRow = ({ spec, index, onChange, onRemove, isOnly }) => (
    <div style={specStyles.row}>
        <div style={specStyles.indexBadge}>{index + 1}</div>

        <div style={specStyles.inputWrap}>
            <input
                list="spec-suggestions"
                placeholder="e.g. Capacity, Watt..."
                value={spec.label}
                onChange={(e) => onChange(index, "label", e.target.value)}
                style={specStyles.input}
            />
        </div>

        <span style={specStyles.colon}>:</span>

        <div style={specStyles.inputWrap}>
            <input
                placeholder="e.g. 1.2L, 1500W..."
                value={spec.value}
                onChange={(e) => onChange(index, "value", e.target.value)}
                style={specStyles.input}
            />
        </div>

        <button
            type="button"
            onClick={() => onRemove(index)}
            style={{ ...specStyles.removeBtn, visibility: isOnly ? "hidden" : "visible" }}
            title="Remove"
        >
            ✕
        </button>
    </div>
);

/* ── main component ─────────────────────────────────────────────────── */
const UpdateProductOne = ({
                              onNext,
                              product,
                              name,            setName,
                              category,        setCategory,
                              desc,            setDesc,
                              seo,             setSeo,
                              modelNum,        setModelNum,
                              isActive,        setIsActive,
                              featured,        setFeatured,
                              selectedOption,  setSelectedOption,
                              productLocation, setProductLocation,
                              specs,           setSpecs,
                          }) => {
    const dispatch = useDispatch();

    useEffect(() => { dispatch(getAllCategories()); }, [dispatch]);
    useEffect(() => { dispatch(getallLang()); },      [dispatch]);

    const data  = useSelector((state) => state?.category?.category?.data);
    const langs = useSelector((state) => state?.Lang?.lang?.langs);

    const options = langs?.map((l) => ({ value: l.id, label: l.name }));

    /* ── spec handlers ── */
    const handleSpecChange = useCallback((index, field, val) => {
        setSpecs((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: val };
            return updated;
        });
    }, [setSpecs]);

    const addSpec = useCallback(() => {
        setSpecs((prev) => [...prev, { label: "", value: "" }]);
    }, [setSpecs]);

    const removeSpec = useCallback((index) => {
        setSpecs((prev) => prev.filter((_, i) => i !== index));
    }, [setSpecs]);

    const hasPreview = specs.some((s) => s.label || s.value);

    const handleCategoryChange = (e) => {
        setCategory(Number(e.target.value));
    };

    const handleCountryChange = (e) => {
        setProductLocation(e.target.value);
    };

    return (
        <div>
            <ProgressFullBar showImage={false} showSpec={false} completed={25} />

            <div className="AddProductForm">

                {/* Product Name */}
                <div className="FormName">
                    <span>Product Name</span>
                    <input
                        type="text"
                        value={name ?? ""}
                        placeholder="Enter Product Name"
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                {/* SEO */}
                <div className="FormName">
                    <span>SEO</span>
                    <input
                        type="text"
                        placeholder="Enter SEO"
                        value={seo ?? ""}
                        onChange={(e) => setSeo(e.target.value)}
                    />
                </div>

                {/* Category */}
                <div className="selectForm">
                    <label htmlFor="category">Category</label>
                    <select
                        id="category"
                        value={category ?? product?.categories?.[0]?.id ?? ""}
                        onChange={handleCategoryChange}
                    >
                        {data?.map((item, i) => (
                            <option key={i} value={item.id}>{item.name}</option>
                        ))}
                    </select>
                </div>

                {/* Description */}
                <div className="FormName">
                    <span>Description</span>
                    <RichTextEditor
                        value={desc ?? ""}
                        onChange={setDesc}
                        placeholder="Enter Product Description"
                    />
                </div>

                {/* Country */}
                <div className="selectForm">
                    <label htmlFor="Country">Country</label>
                    <select
                        id="Country"
                        value={productLocation ?? ""}
                        onChange={handleCountryChange}
                    >
                        <option value="">Select Country</option>
                        {Countries?.map((item, i) => (
                            <option key={i} value={item?.code}>{item.name}</option>
                        ))}
                    </select>
                </div>

                {/* Model Number */}
                <div className="FormName">
                    <span>Model No.</span>
                    <input
                        type="text"
                        placeholder="Enter Model Number"
                        value={modelNum ?? ""}
                        onChange={(e) => setModelNum(e.target.value)}
                    />
                </div>

                {/* ── Product Specifications ──────────────────────────────────── */}
                <div className="FormName">
                    <span>Product Specifications</span>
                    <p style={specStyles.hint}>
                        Edit technical specs displayed on the product card (Capacity, Watt, Ampere, etc.)
                    </p>

                    <datalist id="spec-suggestions">
                        {SPEC_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                    </datalist>

                    {/* Column headers */}
                    <div style={specStyles.headers}>
                        <div style={{ width: 28 }} />
                        <div style={{ ...specStyles.headerLabel, flex: 1 }}>Spec Name</div>
                        <div style={{ width: 20 }} />
                        <div style={{ ...specStyles.headerLabel, flex: 1 }}>Value</div>
                        <div style={{ width: 30 }} />
                    </div>

                    {/* Rows */}
                    <div style={specStyles.list}>
                        {specs.map((spec, i) => (
                            <SpecRow
                                key={i}
                                spec={spec}
                                index={i}
                                onChange={handleSpecChange}
                                onRemove={removeSpec}
                                isOnly={specs.length === 1}
                            />
                        ))}
                    </div>

                    {/* Add row */}
                    <button type="button" onClick={addSpec} style={specStyles.addBtn}>
                        + Add Specification
                    </button>

                    {/* Live preview */}
                    {hasPreview && (
                        <div style={specStyles.preview}>
                            <p style={specStyles.previewTitle}>Card Preview</p>
                            <div style={specStyles.previewGrid}>
                                {specs
                                    .filter((s) => s.label || s.value)
                                    .map((s, i) => (
                                        <div key={i} style={specStyles.previewChip}>
                                            <span style={specStyles.previewKey}>{s.label || "—"}</span>
                                            <span style={specStyles.previewVal}>{s.value || "—"}</span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
                {/* ── End Specifications ─────────────────────────────────────── */}

                {/* Language */}
                <div style={{ width: "10vw" }}>
                    <p>Language</p>
                    <Select
                        value={selectedOption}
                        onChange={setSelectedOption}
                        options={options}
                    />
                </div>

                <div className="AddProductBtn">
                    <button onClick={onNext}>Next</button>
                </div>

            </div>
        </div>
    );
};

/* ── Spec section styles ─────────────────────────────────────────────── */
const specStyles = {
    hint: {
        margin: "2px 0 10px",
        fontSize: 12,
        color: "#6b7280",
    },
    headers: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 4,
    },
    headerLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: "#374151",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    list: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
    },
    row: {
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    indexBadge: {
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: "#e5e7eb",
        color: "#6b7280",
        fontSize: 11,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    inputWrap: { flex: 1 },
    input: {
        width: "100%",
        padding: "7px 10px",
        border: "1px solid #d1d5db",
        borderRadius: 6,
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
    },
    colon: {
        fontSize: 18,
        color: "#9ca3af",
        width: 12,
        textAlign: "center",
        flexShrink: 0,
    },
    removeBtn: {
        width: 26,
        height: 26,
        borderRadius: "50%",
        border: "none",
        background: "#fee2e2",
        color: "#dc2626",
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
    },
    addBtn: {
        marginTop: 10,
        padding: "7px 16px",
        background: "#f0fdf4",
        border: "1px dashed #86efac",
        borderRadius: 6,
        color: "#16a34a",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        width: "100%",
    },
    preview: {
        marginTop: 14,
        padding: "10px 14px",
        background: "#f8f9fa",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
    },
    previewTitle: {
        margin: "0 0 8px",
        fontSize: 11,
        fontWeight: 700,
        color: "#9ca3af",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    previewGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
    },
    previewChip: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "6px 14px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        minWidth: 80,
    },
    previewKey: {
        fontSize: 11,
        color: "#6b7280",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },
    previewVal: {
        fontSize: 15,
        fontWeight: 700,
        color: "#111827",
        marginTop: 2,
    },
};

export default UpdateProductOne;